import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { ITeleopJoystickConfiguration } from "@formant/core/model/ITeleopJoystickConfiguration";
import { ITwist } from "@formant/core/model/ITwist";
import { createRtcStreamMessage } from "@formant/rtc-client/utils/createRtcStreamMessage";

import { track } from "../../../../analytics";
import { deviceStore, operateStore } from "../../../../stores";
import { ITeleopSession } from "../../../../teleop/ITeleopSession";
import { formatTime } from "../../../../utils/formatTime";
import { Heading3 } from "../../../common/Heading3";
import * as styles from "./index.scss";
import { ITeleopTwistValue } from "./ITeleopTwistValue";
import { TeleopJoystick } from "./TeleopJoystick";

export interface ITeleopJoysticksProps {
    session?: ITeleopSession;
    velocityStream?: IRtcStream;
    configuration: ITeleopJoystickConfiguration[];
    debug?: boolean;
    className?: string;
}

const zeroTwist: ITwist = {
    linear: {
        x: 0,
        y: 0,
        z: 0,
    },
    angular: {
        x: 0,
        y: 0,
        z: 0,
    },
};

const isZeroTwist = (twist: ITwist) => {
    return (
        twist.linear.x === 0 &&
        twist.linear.y === 0 &&
        twist.linear.z === 0 &&
        twist.angular.x === 0 &&
        twist.angular.y === 0 &&
        twist.angular.z === 0
    );
};

@observer
export class TeleopJoysticks extends Component<ITeleopJoysticksProps> {
    @observable
    private twist?: ITwist = zeroTwist;
    @observable
    private lastMessageDate?: Date;

    private lastTwistWasZero: boolean = false;

    public render() {
        const { configuration, debug, className } = this.props;
        const { lastMessageDate } = this;

        const leftJoystick = configuration.find(_ => _.position === "left");
        const rightJoystick = configuration.find(_ => _.position === "right");
        const twist = this.twist || zeroTwist;

        const debugRows = Object.entries(twist).map(_ => (
            <div key={_[0]} className={styles.group}>
                <div className={styles.rows}>
                    <div className={styles.header}>{_[0]}</div>
                    {Object.entries(_[1]).map(row => {
                        const label = row[0] as string;
                        const value = row[1] as number;
                        return (
                            <div key={"row_" + label} className={styles.row}>
                                <div className={styles.label}>{label}:</div>
                                <div
                                    className={classnames(
                                        styles.value,
                                        value && styles["has-value"],
                                    )}
                                >
                                    {value.toFixed(3)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        ));

        const armed =
            debug ||
            (operateStore.armed &&
                !operateStore.editingDevice &&
                !operateStore.feedbackVisible &&
                !deviceStore.issuingCommand &&
                !operateStore.usingTerminal);

        return (
            <>
                <div
                    className={classnames(
                        styles["teleop-joysticks"],
                        debug && styles.debug,
                        !armed && !debug && styles.disabled,
                        className && className,
                    )}
                >
                    {leftJoystick ? (
                        <TeleopJoystick
                            joystickConfiguration={leftJoystick}
                            className={styles.left}
                            onSendTwistValues={this.onSendTwistValues}
                            debug={debug}
                            armed={armed}
                        />
                    ) : (
                        <div />
                    )}
                    {rightJoystick && (
                        <TeleopJoystick
                            joystickConfiguration={rightJoystick}
                            className={styles.right}
                            onSendTwistValues={this.onSendTwistValues}
                            debug={debug}
                            armed={armed}
                        />
                    )}
                </div>
                {debug && (
                    <div className={styles.data}>
                        <Heading3>Output</Heading3>
                        {debugRows}
                        <div className={styles.group}>
                            <div className={styles.rows}>
                                <div className={styles.row}>
                                    <div className={styles.header}>Time</div>
                                    <div className={styles.value}>
                                        {lastMessageDate
                                            ? formatTime(lastMessageDate)
                                            : "..."}
                                    </div>
                                    <div />
                                    <div />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    private onSendTwistValues = (
        twistValues: ITeleopTwistValue[],
        reliable: boolean,
    ) => {
        const { twist } = this;
        const { session, velocityStream } = this.props;
        const newTwist: ITwist = {
            linear: {
                x: twist?.linear.x || 0,
                y: twist?.linear.y || 0,
                z: twist?.linear.z || 0,
            },
            angular: {
                x: twist?.angular.x || 0,
                y: twist?.angular.y || 0,
                z: twist?.angular.z || 0,
            },
        };

        twistValues.forEach(({ dimension, value }) => {
            if (dimension === "linear-x") {
                newTwist.linear.x = value;
            }
            if (dimension === "linear-y") {
                newTwist.linear.y = value;
            }
            if (dimension === "linear-z") {
                newTwist.linear.z = value;
            }
            if (dimension === "angular-x") {
                newTwist.angular.x = value;
            }
            if (dimension === "angular-y") {
                newTwist.angular.y = value;
            }
            if (dimension === "angular-z") {
                newTwist.angular.z = value;
            }
        });

        const isZero = isZeroTwist(newTwist);
        if (isZero && this.lastTwistWasZero) {
            return;
        }
        this.lastTwistWasZero = isZero;

        this.twist = newTwist;

        this.lastMessageDate = new Date();

        if (!session || !velocityStream) {
            return;
        }

        const message = createRtcStreamMessage(velocityStream, {
            twist: this.twist,
        });

        this.trackJoystick();

        session.send(message, { channelLabel: "stream.latest-ttl" });
        if (reliable) {
            session.send(message, { channelLabel: "stream.latest-reliable" });
        }
    };

    private trackJoystick = throttle(
        () => track("Joystick Moved"),
        5 * duration.second,
    );
}
